import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import {
  ArrowLeft,
  Users,
  UserCog,
  Calendar as CalendarIcon,
  BarChart3,
  Trophy,
  AlertCircle,
  Plus,
  RefreshCw,
  Clock,
  ChevronRight,
  ClipboardList
} from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import { useAuth } from '../../hooks/useAuth';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { clubCapturaEsteTorneo } from '../../lib/torneoOrigen';
import { puedeCapturarJuegos } from '../../lib/permisosJuegos';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Alert,
  AlertTitle,
  AlertDescription,
  Button,
  Input
} from '../ui';
import {
  getInscripcionById,
  getRosterByInscripcion,
  precargarRosterDesdeGrupo,
  upsertRosterJugador,
  removeRosterJugador,
  getGroupPlayers,
  getJuegosByGrupo,
  getStaffGroups,
  jugadoresSinFirmaParaTorneos
} from '../../lib/supabase';
import { parseLocalDate, getMexicoDateString } from '../../lib/dateUtils';
import { rosterCongelado, limitesDeRoster } from '../../lib/rosterCongelado';
import { cn, fixMojibake } from '../../lib/utils';
import { validarRoster, getMisCambiosRoster, getElegiblesPlayoff } from '../../lib/services/torneoRoster';
import { MENSAJE_CONDICIONES_TORNEO } from '../../lib/torneoErrores';
import SolicitarCambioRosterDialog from './SolicitarCambioRosterDialog';
import NumerosGrupoEditor from '../groups/NumerosGrupoEditor';
import StaffInscripcionTab from './StaffInscripcionTab';

const ROSTER_STATUS = [
  { value: 'convocado', label: 'Convocado', color: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600' },
  { value: 'lesionado', label: 'Lesionado', color: 'border-amber-500/30 bg-amber-500/10 text-amber-600' },
  { value: 'suspendido', label: 'Suspendido', color: 'border-rose-500/30 bg-rose-500/10 text-rose-600' },
  { value: 'no_convocado', label: 'No convocado', color: 'border-slate-500/30 bg-slate-500/10 text-slate-600' }
];

// Quien juega el torneo: una fila del roster de la inscripcion que no es "no convocado". Es lo
// que cuenta la regla firma_tutor de validar_roster_inscripcion (pieza 2b), asi que solo a ellos
// se les pide y se les marca la firma del tutor; a quien solo esta en el grupo, no.
const juegaElTorneo = (filaRoster) => !!filaRoster && filaRoster.status !== 'no_convocado';

const CAMBIO_TIPO_LABELS = { alta: 'Alta', baja: 'Baja', edicion: 'Edición' };

const CAMBIO_ESTADO_BADGE = {
  pendiente: 'border-amber-500/30 bg-amber-500/10 text-amber-600',
  aprobado: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600',
  rechazado: 'border-rose-500/30 bg-rose-500/10 text-rose-600'
};

// Los triggers de la base (tope, roster congelado, torneo archivado) traen el motivo
// real y es accionable: taparlo con el generico deja al usuario sin saber que corregir.
// El resto es ruido tecnico. Mismo criterio que los otros cuatro handlers de la pantalla.
const TAB_CONFIG = [
  { id: 'roster', label: 'Roster', icon: Users },
  // Solo en la red: el staff que va al torneo (20260907). En interno el staff es el del grupo.
  { id: 'staff', label: 'Staff', icon: UserCog, soloRed: true },
  { id: 'partidos', label: 'Juegos', icon: CalendarIcon },
  { id: 'estadisticas', label: 'Estadísticas', icon: BarChart3 }
];

const InscripcionDetail = () => {
  const { torneoId, inscripcionId } = useParams();
  const navigate = useNavigate();
  const { staff } = useAuth();
  const { role, isPlatformAdmin, canManageTorneos } = usePermissions();
  const { confirm, alert, ConfirmDialog } = useConfirmDialog();

  const [inscripcion, setInscripcion] = useState(null);
  const [roster, setRoster] = useState([]);
  const [grupoJugadores, setGrupoJugadores] = useState([]);
  const [partidos, setPartidos] = useState([]);
  const [staffGroups, setStaffGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('roster');
  // Si el grupo ya tiene juegos, se entra por Juegos (lo que el coach viene a ver). Una
  // vez que el usuario elige pestana, no se le vuelve a mover.
  const tabElegidaRef = useRef(false);
  const [savingPrecarga, setSavingPrecarga] = useState(false);
  const [violaciones, setViolaciones] = useState([]);
  const [cambios, setCambios] = useState([]);
  const [solicitarOpen, setSolicitarOpen] = useState(false);
  // Torneo interno: los jugadores del grupo son el roster; solo se editan sus numeros.
  const numerosSaveRef = useRef(null);
  const [numerosEstado, setNumerosEstado] = useState(null);
  const [guardandoNumeros, setGuardandoNumeros] = useState(false);
  const [inelegibles, setInelegibles] = useState(() => new Set());
  // Pieza 2b: jugadores del roster cuya firma no cubre torneos (solo torneos de la red) y si la
  // lectura fallo.
  const [sinFirmaTorneo, setSinFirmaTorneo] = useState(() => new Set());
  const [firmasError, setFirmasError] = useState(false);

  // La pantalla de "Cargando..." solo en la PRIMERA carga. Cada accion (marcar un
  // jugador, un numero, el staff) recargaba los datos con loading=true y la pantalla
  // entera se desmontaba: se perdian scroll y foco, se sentia como una recarga de la
  // pagina (el dueno, 2026-09-07). Las recargas siguientes son silenciosas.
  const cargadoRef = useRef(false);
  const loadAll = useCallback(async () => {
    if (!inscripcionId) return;
    if (!cargadoRef.current) setLoading(true);
    setError(null);
    try {
      const inscRes = await getInscripcionById(inscripcionId);
      if (inscRes.error) throw inscRes.error;
      setInscripcion(inscRes.data);

      const grupoId = inscRes.data?.grupo_id;
      const [rosterRes, gpRes, partidosRes, valRes, cambiosRes] = await Promise.all([
        getRosterByInscripcion(inscripcionId),
        grupoId ? getGroupPlayers(grupoId) : Promise.resolve({ data: [] }),
        grupoId ? getJuegosByGrupo(grupoId).catch(() => []) : Promise.resolve([]),
        validarRoster(inscripcionId),
        getMisCambiosRoster(inscripcionId)
      ]);
      setRoster(rosterRes.data || []);
      setViolaciones(valRes.data || []);
      setCambios(cambiosRes.data || []);
      // Aplanar: getGroupPlayers devuelve filas de jugadores_grupos con jugadores anidado
      const flatJugadores = (gpRes.data || [])
        .map((row) => row.jugadores ? { ...row.jugadores, jugador_id: row.jugador_id } : null)
        .filter(Boolean);
      setGrupoJugadores(flatJugadores);
      // En un torneo de la red se marca a quien juega el torneo sin la firma del tutor que cubre
      // torneos (la base lo avisa sin bloquear: regla firma_tutor, severidad warn, contrato H).
      // Como esa regla, cuenta el roster del torneo (no "no convocado"), no a todo el grupo. En
      // el interno no aplica (contrato B2). Si la lectura falla se dice; no se pinta a nadie
      // como si estuviera al dia.
      const idsQueJuegan = (rosterRes.data || []).filter(juegaElTorneo).map((r) => r.jugador_id);
      if (!clubCapturaEsteTorneo(inscRes.data?.torneos, inscRes.data?.club_org_id) && idsQueJuegan.length) {
        const firmas = await jugadoresSinFirmaParaTorneos(idsQueJuegan);
        setSinFirmaTorneo(new Set(firmas.data || []));
        setFirmasError(!!firmas.error);
      } else {
        setSinFirmaTorneo(new Set());
        setFirmasError(false);
      }
      const partidosData = Array.isArray(partidosRes) ? partidosRes : (partidosRes.data || []);
      const partidosDelTorneo = partidosData.filter((p) => p.torneo_id === inscRes.data?.torneo_id);
      setPartidos(partidosDelTorneo);
      if (!tabElegidaRef.current && partidosDelTorneo.length > 0) setActiveTab('partidos');

      if (inscRes.data?.torneos?.playoff_requiere_participacion) {
        const { data: elegibles } = await getElegiblesPlayoff(inscripcionId);
        setInelegibles(new Set((elegibles || []).filter((r) => !r.elegible).map((r) => r.jugador_id)));
      } else {
        setInelegibles(new Set());
      }
    } catch (err) {
      console.error('Error cargando inscripción:', err);
      setError('No pudimos cargar la inscripción.');
    } finally {
      setLoading(false);
      cargadoRef.current = true;
    }
  }, [inscripcionId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Asignaciones del staff en sesion, para preguntar por grupo (no por rol) si
  // puede gestionar el roster de ESTE grupo. Admin y platform_admin no las necesitan:
  // puedeCapturarJuegos ya los deja pasar antes de mirar el arreglo.
  useEffect(() => {
    if (!staff?.id) return;
    getStaffGroups(staff.id).then(({ data }) => setStaffGroups(data || []));
  }, [staff?.id]);

  const grupo = inscripcion?.grupos;
  const torneo = inscripcion?.torneos;
  const orgId = inscripcion?.club_org_id;

  // "Nuevo partido" se decidia solo por el rol del usuario, sin preguntar quien
  // organiza -- el mismo hueco que 7da21a0 cerro en las demas pantallas. Solo en un
  // torneo de la red el rol lo programa la organizadora y el trigger _juegos_guard
  // rechaza la escritura: ahi el boton prometeria algo que la base va a negar. En el
  // torneo interno (sin organizadora) el club captura, y el boton va.
  const puedeCrearPartido = clubCapturaEsteTorneo(torneo, orgId);
  // En el torneo interno no hay roster aparte (el dueno, 2026-09-07): los jugadores del
  // grupo SON el roster, y lo unico propio del torneo es el numero con el que juegan en
  // este grupo. La pestana Roster se vuelve "Jugadores y numeros".
  const esInterno = clubCapturaEsteTorneo(torneo, orgId);
  const guardarNumeros = async () => {
    if (!numerosSaveRef.current) return;
    try {
      setGuardandoNumeros(true);
      const res = await numerosSaveRef.current();
      if (res?.error) throw res.error;
      await alert('Listo', 'Números guardados.');
    } catch (err) {
      console.error(err);
      await alert('No se pudo guardar', err?.message || 'No se pudieron guardar los números.');
    } finally {
      setGuardandoNumeros(false);
    }
  };

  // Solo para lo que la base SI deja pasar por grupo: "Solicitar cambio" (RPC
  // solicitar_cambio_roster_item, pide _es_mi_inscripcion) y "Nuevo partido" (RPC
  // juegos_manage via puede_capturar_juegos). El roster y el staff de la inscripcion
  // siguen en tij_manage/tis_manage = is_org_admin(org_id): la escritura es directo a
  // tabla y solo el admin pasa la RLS, asi que ahi el gate se queda en canManageTorneos
  // -- abrir el boton sin abrir la politica solo cambia el error de "no aparece" a
  // "aparece y falla".
  const puedeGestionar = useMemo(() => puedeCapturarJuegos({
    rol: role,
    esPlatformAdmin: isPlatformAdmin,
    asignaciones: staffGroups,
    grupoId: inscripcion?.grupo_id,
  }), [role, isPlatformAdmin, staffGroups, inscripcion?.grupo_id]);

  // En el torneo interno no hay limites de roster (2026-09-06): el organizador es el
  // propio club, y "Solicitar cambio" seria pedirse permiso a uno mismo. La fecha
  // limite y el tope son de la organizadora de la red.
  const limites = limitesDeRoster(torneo, orgId);
  const deadline = limites.congelable ? (torneo?.fecha_limite_roster || null) : null;
  const congelado = limites.congelable && rosterCongelado(inscripcion, torneo);
  const maxJug = limites.tope;
  // `impide_aprobar` (pieza 2b): la organizadora no puede aprobar el equipo hasta que se corrija
  // (convenio 1.1 sin aceptar). Van primero. La firma del tutor llega como `warn` y sale con los
  // avisos (contrato H).
  const bloqueos = [
    ...violaciones.filter((v) => v.severidad === 'impide_aprobar'),
    ...violaciones.filter((v) => v.severidad === 'block'),
  ];
  const avisos = violaciones.filter((v) => v.severidad === 'warn');

  const rosterMap = useMemo(() => {
    const m = new Map();
    roster.forEach((r) => m.set(r.jugador_id, r));
    return m;
  }, [roster]);
  // "Falta firma del tutor" solo para quien juega el torneo (ver juegaElTorneo).
  const faltaFirma = (jugadorId) => juegaElTorneo(rosterMap.get(jugadorId)) && sinFirmaTorneo.has(jugadorId);
  const hayFaltaFirma = grupoJugadores.some((j) => faltaFirma(j.id));

  const proximoPartido = useMemo(() => {
    const hoy = getMexicoDateString();
    return partidos
      .filter((p) => (p.fecha || '0') >= hoy && p.estatus !== 'terminado' && p.estatus !== 'cancelado')
      .sort((a, b) => (a.fecha || '').localeCompare(b.fecha || ''))[0];
  }, [partidos]);

  const handlePrecargar = async () => {
    if (!inscripcionId || !grupo?.id) return;
    setSavingPrecarga(true);
    try {
      // precargarRosterDesdeGrupo no lanza: atrapa y devuelve { error }. Ignorar ese
      // resultado (como antes) es el falso exito -- la RLS rechaza el upsert y aqui
      // igual se avisaba "Listo" con el roster vacio. Precargar inserta TODOS los
      // jugadores activos del grupo de una vez, asi que reventar el tope
      // (_roster_tij_enforce) es el caso probable, no el raro -- por eso, igual que
      // los handlers hermanos de aqui abajo, se muestra el motivo real cuando lo trae.
      const { error } = await precargarRosterDesdeGrupo(inscripcionId, grupo.id, orgId);
      if (error) {
        const msg = error.message || '';
        await alert('Error', /Dorsal|tope|congelado|archivado/i.test(msg) ? msg : 'No se pudo precargar el roster.');
        return;
      }
      await loadAll();
      await alert('Listo', 'Roster precargado desde el grupo.');
    } catch (err) {
      console.error(err);
      await alert('Error', 'No se pudo precargar el roster.');
    } finally {
      setSavingPrecarga(false);
    }
  };

  const handleToggleJugador = async (jugadorId, currentRoster) => {
    if (!canManageTorneos) return;
    try {
      if (currentRoster) {
        const { error } = await removeRosterJugador(inscripcionId, jugadorId);
        if (error) {
          const msg = error.message || '';
          await alert('No se pudo actualizar', /Dorsal|tope|congelado/i.test(msg) ? msg : 'No se pudo actualizar el jugador.');
          return;
        }
      } else {
        const { error } = await upsertRosterJugador({
          inscripcionId,
          jugadorId,
          orgId,
          status: 'convocado'
        });
        if (error) {
          const msg = error.message || '';
          await alert('No se pudo actualizar', /Dorsal|tope|congelado/i.test(msg) ? msg : 'No se pudo actualizar el jugador.');
          return;
        }
      }
      await loadAll();
    } catch (err) {
      console.error(err);
      const msg = err?.message || '';
      await alert('No se pudo actualizar', /Dorsal|tope|congelado/i.test(msg) ? msg : 'No se pudo actualizar el jugador.');
    }
  };

  const handleStatusChange = async (jugadorId, newStatus) => {
    if (!canManageTorneos) return;
    try {
      const { error } = await upsertRosterJugador({
        inscripcionId,
        jugadorId,
        orgId,
        status: newStatus
      });
      if (error) {
        const msg = error.message || '';
        await alert('No se pudo actualizar', /Dorsal|tope|congelado/i.test(msg) ? msg : 'No se pudo actualizar el estado.');
        return;
      }
      await loadAll();
    } catch (err) {
      console.error(err);
      const msg = err?.message || '';
      await alert('No se pudo actualizar', /Dorsal|tope|congelado/i.test(msg) ? msg : 'No se pudo actualizar el estado.');
    }
  };

  const handleDorsalChange = async (jugadorId, dorsal) => {
    if (!canManageTorneos) return;
    const dorsalNum = parseInt(dorsal, 10);
    try {
      const { error } = await upsertRosterJugador({
        inscripcionId,
        jugadorId,
        orgId,
        dorsalTorneo: isNaN(dorsalNum) ? null : dorsalNum
      });
      if (error) {
        const msg = error.message || '';
        await alert('No se pudo actualizar', /Dorsal|tope|congelado/i.test(msg) ? msg : 'No se pudo actualizar el dorsal.');
        await loadAll();
        return;
      }
      await loadAll();
    } catch (err) {
      console.error(err);
      const msg = err?.message || '';
      await alert('No se pudo actualizar', /Dorsal|tope|congelado/i.test(msg) ? msg : 'No se pudo actualizar el dorsal.');
      await loadAll();
    }
  };

  if (loading) {
    return (
      <FeaturedLayout title="Inscripción">
        <div className="py-20 text-center text-muted-foreground">Cargando...</div>
      </FeaturedLayout>
    );
  }

  if (error || !inscripcion) {
    return (
      <FeaturedLayout title="Inscripción">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error || 'Inscripción no encontrada.'}</AlertDescription>
        </Alert>
        <Button variant="outline" className="mt-4" onClick={() => navigate(`/torneos/${torneoId}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver
        </Button>
      </FeaturedLayout>
    );
  }

  const convocados = roster.filter((r) => r.status === 'convocado').length;
  const lesionados = roster.filter((r) => r.status === 'lesionado').length;
  const suspendidos = roster.filter((r) => r.status === 'suspendido').length;
  // Tres cosas distintas se llaman "puntos" en una tabla, y conviene no confundirlas:
  //   PF  puntos ANOTADOS en los partidos   (puntos_favor)
  //   PC  puntos RECIBIDOS                  (puntos_contra)
  //   PTS puntos DE LA TABLA, esto de aqui  (3 por ganar, 1 por empatar)
  // La columna se llamaba `goles_*` hasta 20260904. Los cuatro deportes de la plataforma
  // hablan de PUNTOS; el gol solo existe en soccer, y ahi es una forma de anotar puntos,
  // no el marcador.
  const puntos = (inscripcion.ganados || 0) * 3 + (inscripcion.empatados || 0);

  return (
    <FeaturedLayout title={fixMojibake(grupo?.nombre_del_grupo || 'Inscripción')}>
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/torneos/${torneoId}`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {fixMojibake(torneo?.nombre || 'Torneo')}
        </Button>

        <Card className="border shadow-sm">
          <CardHeader className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-600" />
              <CardTitle className="text-xl">{fixMojibake(grupo?.nombre_del_grupo || 'Grupo')}</CardTitle>
              {grupo?.categoria_base && (
                <Badge variant="secondary">{fixMojibake(grupo.categoria_base)}</Badge>
              )}
              {grupo?.rama && <Badge variant="outline">{fixMojibake(grupo.rama)}</Badge>}
            </div>
            <p className="text-xs text-muted-foreground">
              en <strong>{fixMojibake(torneo?.nombre || '')}</strong>
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-2">
              <RecordStat label="PJ" value={inscripcion.partidos_jugados || 0} />
              <RecordStat label="G-E-P" value={`${inscripcion.ganados || 0}-${inscripcion.empatados || 0}-${inscripcion.perdidos || 0}`} />
              <RecordStat label="PF" value={inscripcion.puntos_favor || 0} />
              <RecordStat label="PC" value={inscripcion.puntos_contra || 0} />
              <RecordStat label="PTS" value={puntos} highlight />
            </div>

            {proximoPartido && (
              <div className="rounded-lg border bg-amber-50 border-amber-200 p-3 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wide text-amber-700">Próximo juego</p>
                  <p className="text-sm font-medium truncate">
                    {proximoPartido.fecha ? format(parseLocalDate(proximoPartido.fecha) || new Date(), "EEE d MMM", { locale: es }) : 'Sin fecha'}
                    {proximoPartido.hora && ` · ${proximoPartido.hora}`}
                    {' · vs '}
                    {fixMojibake(proximoPartido.equipo?.nombre || proximoPartido.equipo_nombre || 'Rival')}
                  </p>
                </div>
                <Button size="sm" onClick={() => navigate(`/torneos/juegos/${proximoPartido.id}`)}>
                  <ClipboardList className="h-4 w-4 mr-1" /> Asistencia
                </Button>
              </div>
            )}
          </CardHeader>
        </Card>

        <div className="rounded-xl border bg-muted/30 p-2 flex flex-wrap gap-2">
          {TAB_CONFIG.filter((tab) => !(tab.soloRed && esInterno)).map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => { tabElegidaRef.current = true; setActiveTab(tab.id); }}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition',
                  active ? 'bg-background shadow border border-border' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.id === 'roster' && esInterno ? 'Jugadores y números' : tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'roster' && esInterno && (
          <Card className="border shadow-sm">
            <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-base">Jugadores y números</CardTitle>
                <p className="mt-1 text-xs text-muted-foreground">
                  En un torneo interno los jugadores del grupo son el roster. El número es el de este
                  grupo; si no lo cambias, es el del club. Es el que sale en el tarjetón y en los uniformes.
                </p>
              </div>
              {puedeGestionar && (
                <Button size="sm" onClick={guardarNumeros} disabled={guardandoNumeros || Boolean(numerosEstado?.hayRepetidos)}>
                  {guardandoNumeros ? 'Guardando...' : 'Guardar números'}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <NumerosGrupoEditor
                grupoId={inscripcion?.grupo_id}
                onEstadoChange={setNumerosEstado}
                saveRef={numerosSaveRef}
              />
            </CardContent>
          </Card>
        )}

        {activeTab === 'roster' && !esInterno && (
          <div className="space-y-3">
            {congelado && (
              <Alert className="border-amber-300 bg-amber-50">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Roster congelado</AlertTitle>
                <AlertDescription>
                  <div className="space-y-2">
                    <p>
                      {deadline ? `La fecha límite (${new Date(`${deadline}T00:00:00`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}) ya pasó. ` : ''}
                      Los cambios ahora pasan por aprobación del organizador.
                    </p>
                    {puedeGestionar && (
                      <Button variant="outline" size="sm" onClick={() => setSolicitarOpen(true)}>
                        Solicitar cambio
                      </Button>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}
            {congelado && cambios.length > 0 && (
              <Card className="border shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">Solicitudes de cambio</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {cambios.map((c) => (
                    <div key={c.id} className="flex items-start justify-between gap-3 rounded-lg border p-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {CAMBIO_TIPO_LABELS[c.tipo] || c.tipo}
                          {' · '}
                          {fixMojibake(c.jugador_nombre || 'Sin nombre')}
                          {c.dorsal_torneo != null && ` · #${c.dorsal_torneo}`}
                        </p>
                        {c.nota && <p className="text-xs text-muted-foreground mt-0.5">{fixMojibake(c.nota)}</p>}
                        {c.estado === 'rechazado' && c.motivo_rechazo && (
                          <p className="text-xs text-rose-600 mt-0.5">Motivo: {fixMojibake(c.motivo_rechazo)}</p>
                        )}
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                        </p>
                      </div>
                      <Badge variant="outline" className={cn('shrink-0', CAMBIO_ESTADO_BADGE[c.estado] || '')}>
                        {c.estado || 'pendiente'}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            {bloqueos.map((v, i) => (
              <Alert key={`b${i}`} variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {v.detalle}
                  {v.regla === 'condiciones' && (
                    <span className="mt-1 block">{MENSAJE_CONDICIONES_TORNEO}</span>
                  )}
                </AlertDescription>
              </Alert>
            ))}
            {avisos.map((v, i) => (
              <Alert key={`w${i}`} className="border-amber-300 bg-amber-50">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{v.detalle}</AlertDescription>
              </Alert>
            ))}
            {firmasError && (
              <Alert className="border-amber-300 bg-amber-50">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>No se pudo revisar la firma de los tutores. Recarga la página.</AlertDescription>
              </Alert>
            )}
          <Card className="border shadow-sm">
            <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-base">Roster del torneo</CardTitle>
                <div className="text-xs text-muted-foreground mt-1">
                  {convocados} convocados · {lesionados} lesionados · {suspendidos} suspendidos
                  {maxJug ? (
                    <span className={cn(convocados > maxJug && 'text-destructive font-medium')}>
                      {` · ${convocados}/${maxJug} tope`}
                    </span>
                  ) : ''}
                </div>
                {hayFaltaFirma && (
                  <p className="text-xs text-amber-700 mt-1">
                    Quien tiene “Falta firma del tutor” sí juega el torneo, pero hay que pedirle a su tutor que
                    firme el Aviso de Privacidad nuevo. El administrador del club se la pide desde Consentimientos.
                    El rival no ve su foto hasta que su tutor firme.
                  </p>
                )}
              </div>
              {canManageTorneos && !congelado && (
                <Button variant="outline" size="sm" onClick={handlePrecargar} disabled={savingPrecarga}>
                  <RefreshCw className={cn('h-3 w-3 mr-1', savingPrecarga && 'animate-spin')} />
                  Precargar desde grupo
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {grupoJugadores.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  El grupo no tiene jugadores activos.
                </p>
              ) : (
                grupoJugadores.map((j) => {
                  const r = rosterMap.get(j.id);
                  const enRoster = !!r;
                  return (
                    <div
                      key={j.id}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border p-3',
                        enRoster ? 'bg-emerald-50/50 border-emerald-200' : 'bg-muted/20'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={enRoster}
                        onChange={() => handleToggleJugador(j.id, r)}
                        disabled={!canManageTorneos || congelado}
                        className="h-4 w-4"
                      />
                      <Avatar src={j.imagen_url} name={j.nombre_completo} />
                      <div className="flex-1 min-w-0">
                        {/* div y no p: Badge pinta un div, y un div dentro de un p no es HTML valido. */}
                        <div className="text-sm font-medium truncate flex items-center gap-1.5">
                          {fixMojibake(j.nombre_completo || 'Sin nombre')}
                          {enRoster && inelegibles.has(j.id) && (
                            <Badge variant="outline" className="border-rose-500/30 bg-rose-500/10 text-rose-600 text-[10px] shrink-0">
                              No elegible (playoffs)
                            </Badge>
                          )}
                          {faltaFirma(j.id) && (
                            <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-700 text-[10px] shrink-0">
                              Falta firma del tutor
                            </Badge>
                          )}
                        </div>
                        {j.numero && (
                          <p className="text-[10px] text-muted-foreground">Dorsal habitual: {j.numero}</p>
                        )}
                      </div>
                      {enRoster && (
                        <>
                          <Input
                            type="number"
                            placeholder="#"
                            defaultValue={r.dorsal_torneo || ''}
                            onBlur={(e) => handleDorsalChange(j.id, e.target.value)}
                            disabled={!canManageTorneos || congelado}
                            className="w-16 text-center"
                          />
                          <select
                            value={r.status}
                            onChange={(e) => handleStatusChange(j.id, e.target.value)}
                            disabled={!canManageTorneos || congelado}
                            className="text-xs rounded-md border px-2 py-1 bg-white"
                          >
                            {ROSTER_STATUS.map((s) => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === 'staff' && !esInterno && (
          <StaffInscripcionTab
            inscripcionId={inscripcionId}
            grupoId={inscripcion?.grupo_id}
            orgId={orgId}
            tope={torneo?.max_staff_grupo ?? null}
            puedeEditar={puedeGestionar}
            onCambio={loadAll}
          />
        )}

        {activeTab === 'partidos' && (
          <Card className="border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base">Juegos</CardTitle>
              <div className="flex items-center gap-2">
              {/* El rol completo (todos los grupos del club, por dia, para compartir) vive en
                  el torneo. Desde aqui solo se llegaba por la flecha de regreso. */}
              <Button size="sm" variant="outline" onClick={() => navigate(`/torneos/${torneoId}`)}>
                Ver rol del torneo
              </Button>
              {puedeGestionar && puedeCrearPartido && (
                <Button
                  size="sm"
                  onClick={() => navigate('/torneos/juegos', {
                    state: { torneoId, grupoId: grupo?.id, abrirNuevoJuego: true }
                  })}
                >
                  <Plus className="h-4 w-4 mr-1" /> Nuevo juego
                </Button>
              )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {partidos.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Este grupo aún no tiene juegos en el torneo.
                </p>
              ) : (
                partidos
                  .sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''))
                  .map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-2 rounded-lg border p-3 hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/torneos/juegos/${p.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {p.fecha ? format(parseLocalDate(p.fecha) || new Date(), 'dd MMM yy', { locale: es }) : 'Sin fecha'}
                          {p.hora && ` · ${p.hora}`}
                        </div>
                        <p className="text-sm font-medium truncate">
                          vs {fixMojibake(p.equipo?.nombre || p.equipo_nombre || 'Rival')}
                        </p>
                      </div>
                      {p.estatus === 'terminado' && (
                        <div className="text-sm font-bold">{p.puntos_favor} - {p.puntos_contra}</div>
                      )}
                      <Badge variant="outline" className="text-[10px]">{p.estatus || 'programado'}</Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ))
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'estadisticas' && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Juegos" value={inscripcion.partidos_jugados || 0} />
            <StatCard label="Puntos de tabla" value={puntos} />
            <StatCard label="Puntos a favor" value={inscripcion.puntos_favor || 0} />
            <StatCard label="Puntos en contra" value={inscripcion.puntos_contra || 0} />
          </div>
        )}
      </div>

      <ConfirmDialog />
      <SolicitarCambioRosterDialog
        open={solicitarOpen}
        onOpenChange={setSolicitarOpen}
        inscripcionId={inscripcionId}
        grupoJugadores={grupoJugadores}
        rosterMap={rosterMap}
        onSolicitado={() => { setSolicitarOpen(false); loadAll(); }}
      />
    </FeaturedLayout>
  );
};

const Avatar = ({ src, name }) => {
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || '?';
  if (src) {
    return (
      <img
        src={src}
        alt={name || ''}
        className="h-9 w-9 rounded-full object-cover border bg-muted shrink-0"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  return (
    <div className="h-9 w-9 rounded-full bg-muted border flex items-center justify-center text-xs font-semibold text-muted-foreground shrink-0">
      {initials}
    </div>
  );
};

const RecordStat = ({ label, value, highlight }) => (
  <div className={cn('text-center rounded-lg border p-2', highlight && 'bg-amber-50 border-amber-200')}>
    <p className="text-lg font-bold">{value}</p>
    <p className="text-[10px] text-muted-foreground uppercase">{label}</p>
  </div>
);

const StatCard = ({ label, value }) => (
  <Card className="border shadow-sm">
    <CardContent className="py-6 text-center">
      <p className="text-3xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{label}</p>
    </CardContent>
  </Card>
);

export default InscripcionDetail;
